import type { CountryStatus, RegionScore, GeoAsnScore } from '../types/ioda';

function briefFilenameUtc(d: Date): string {
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `irinter-brief-${y}-${mo}-${day}-${h}${mi}-UTC.pdf`;
}

export interface IrinterBriefPdfOptions {
  isFa: boolean;
  generatedAtUtc: Date;
  status: CountryStatus | null;
  /** "Counted from: ... UTC" or null */
  countedFromLine: string | null;
  /** Human-readable clock / duration line */
  outageClockLine: string;
  connectivityPct: number;
  topProvinces: RegionScore[];
  topAsns: GeoAsnScore[];
  mlHealth: { score: number; trend: string } | null;
  methodologyNote: string;
  chartElement: HTMLElement | null;
}

export interface IrinterBriefPdfResult {
  filename: string;
  chartImageCaptured: boolean;
}

export async function generateIrinterBriefPdf(opts: IrinterBriefPdfOptions): Promise<IrinterBriefPdfResult> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const L = opts.isFa;
  let chartImageCaptured = false;
  let y = 44;
  const line = (text: string, size = 10) => {
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, 515);
    doc.text(lines, 40, y);
    y += lines.length * (size * 1.15) + 6;
  };

  doc.setFontSize(18);
  doc.text(L ? 'IRINTER — ایران اینترنت مانیتور' : 'IRINTER — Iran Internet Monitor', 40, y);
  y += 24;
  line(`${L ? 'تولید' : 'Generated'} (UTC): ${opts.generatedAtUtc.toISOString()}`, 9);
  y += 2;
  line(L ? 'خلاصهٔ وضعیت' : 'Situation brief', 11);

  line(opts.outageClockLine, 11);
  if (opts.countedFromLine) line(opts.countedFromLine, 9);

  const st = opts.status?.currentStatus ?? '—';
  line(`${L ? 'وضعیت' : 'Status'}: ${st} · ${L ? 'اتصال' : 'Connectivity'}: ${opts.connectivityPct.toFixed(1)}%`, 11);

  const prov = opts.topProvinces
    .slice()
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((p) => `${p.region_name} (${p.score.toFixed(0)})`)
    .join(', ');
  line(`${L ? '۳ استان پرریسک' : 'Top 3 provinces'}: ${prov || '—'}`, 10);

  const asns = opts.topAsns
    .slice(0, 3)
    .map((a) => `${a.asn ?? a.entity_code} ${a.org ?? ''}`.trim())
    .join('; ');
  line(`${L ? '۳ شبکه برتر' : 'Top 3 networks'}: ${asns || '—'}`, 10);

  if (opts.mlHealth) {
    line(
      `${L ? 'امتیاز ML' : 'ML health'}: ${Math.round(opts.mlHealth.score)}% · ${L ? 'روند' : 'trend'}: ${opts.mlHealth.trend}`,
      10
    );
  }

  line(
    `${L ? 'منابع' : 'Data sources'}: merit-nt, ping-slash24 loss, regional summaries, Cloudflare Radar annotations & traffic.`,
    9
  );

  doc.addPage();
  y = 44;
  doc.setFontSize(14);
  doc.text(L ? 'جداول و نمودار' : 'Tables & chart', 40, y);
  y += 24;

  if (opts.chartElement) {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(opts.chartElement, {
        scale: 1.25,
        backgroundColor: '#0B1120',
        logging: false,
      });
      const img = canvas.toDataURL('image/png', 0.92);
      const maxW = 515;
      const ratio = canvas.height / canvas.width;
      const w = maxW;
      const h = Math.min(220, w * ratio);
      doc.addImage(img, 'PNG', 40, y, w, h);
      chartImageCaptured = true;
      y += h + 16;
    } catch {
      line(L ? '(نمودار در PDF قرار نگرفت)' : '(Chart could not be rasterized)', 9);
    }
  }

  const rows = opts.topAsns.slice(0, 15);
  line(L ? 'وضعیت ISP (نمونه)' : 'ISP snapshot (sample)', 11);
  for (const r of rows) {
    line(`${r.asn ?? r.entity_code} · score ${r.score.toFixed(0)} · events ${r.event_cnt}`, 8);
    if (y > 720) {
      doc.addPage();
      y = 44;
    }
  }

  doc.addPage();
  y = 44;
  doc.setFontSize(14);
  doc.text(L ? 'روش‌شناسی' : 'Methodology', 40, y);
  y += 22;
  line(opts.methodologyNote, 9);

  const filename = briefFilenameUtc(opts.generatedAtUtc);
  doc.save(filename);
  return { filename, chartImageCaptured };
}
